/**
 * @file     ChecklistLesson.tsx
 * @readme   ../README.md
 * @purpose  Checklist lesson type on the academy kit: 52px rows at radius 16 with a check button and an expandable explanation, a MeterRow for progress, then takeaways, the quiz and sources.
 */
'use client';

import '@/core/i18n/deferred/academy';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from 'react-i18next';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { motionTokens } from '@/design-system/tokens/motion';
import { MeterRow } from '@/components/hub/MeterRow';
import { hubColors, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';
import type { LessonContent } from '@/content-system/academy/types';
import { QuizSection } from '../quiz/QuizSection';
import { LegalDisclaimer } from '../LegalDisclaimer';
import { LessonMarkdown } from '../LessonMarkdown';

const calloutSx = {
  px: { xs: 2.5, md: 3 },
  py: 2.5,
  borderRadius: `${layoutTokens.radius.lg}px`,
  backgroundColor: hubColors.surfaceSunken,
} as const;

const CheckGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const ChevronGlyph = ({ open }: { open: boolean }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    sx={{ width: 16, height: 16, transform: open ? 'rotate(180deg)' : 'none', transition: transition('transform'), ...reducedMotionSx }}
  >
    <path d="M6 9l6 6 6-6" />
  </Box>
);

interface ChecklistLessonProps {
  content: LessonContent;
  onContentConsumed?: () => void;
  onQuizPassed?: () => void;
}

export function ChecklistLesson({ content, onContentConsumed, onQuizPassed }: ChecklistLessonProps) {
  const { t } = useTranslation();
  const items = content.checklist ?? [];
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked = items.length > 0 && checked.size === items.length;

  useEffect(() => {
    if (allChecked) onContentConsumed?.();
  }, [allChecked, onContentConsumed]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {content.readingBody && <LessonMarkdown>{content.readingBody}</LessonMarkdown>}

      {items.length > 0 && (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item) => {
            const isChecked = checked.has(item.id);
            const isExpanded = expanded === item.id;
            const panelId = `checklist-${item.id}`;
            return (
              <Box
                component="li"
                key={item.id}
                sx={{
                  borderRadius: `${layoutTokens.radius.md}px`,
                  border: `1px solid ${isChecked ? hubColors.statusSuccess : hubColors.borderStrong}`,
                  backgroundColor: isChecked ? hubColors.successSoft : hubColors.surfaceCard,
                  overflow: 'hidden',
                  transition: transition('border-color', 'background-color'),
                  ...reducedMotionSx,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 52, pl: 2 }}>
                  <Box
                    component="button"
                    type="button"
                    role="checkbox"
                    aria-checked={isChecked}
                    aria-label={item.label}
                    onClick={() => toggle(item.id)}
                    sx={{
                      all: 'unset',
                      boxSizing: 'border-box',
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      width: 22,
                      height: 22,
                      borderRadius: `${layoutTokens.radius.pill}px`,
                      border: `1.5px solid ${isChecked ? hubColors.statusSuccess : hubColors.borderStrong}`,
                      backgroundColor: isChecked ? hubColors.statusSuccess : 'transparent',
                      color: hubColors.textOnPrimary,
                      cursor: 'pointer',
                      transition: transition('border-color', 'background-color'),
                      '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
                      ...reducedMotionSx,
                    }}
                  >
                    {isChecked ? <CheckGlyph /> : null}
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    onClick={() => setExpanded(isExpanded ? null : item.id)}
                    sx={{
                      all: 'unset',
                      boxSizing: 'border-box',
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      minHeight: 52,
                      pr: 2,
                      py: 1.25,
                      fontSize: 15,
                      lineHeight: 1.4,
                      fontWeight: fontWeightTokens.medium,
                      color: isChecked ? hubColors.textSecondary : hubColors.textPrimary,
                      cursor: 'pointer',
                      '& svg': { flexShrink: 0, color: hubColors.textMuted },
                      '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: -2 },
                    }}
                  >
                    <span>{item.label}</span>
                    <ChevronGlyph open={isExpanded} />
                  </Box>
                </Box>
                <Collapse in={isExpanded} timeout={motionTokens.durations.normal}>
                  <Box id={panelId} component="p" sx={{ m: 0, px: 2, pb: 2, pl: 7, fontSize: 14, lineHeight: 1.55, color: hubColors.textSecondary }}>
                    {item.explanation}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {items.length > 0 && (
        <MeterRow
          label={t('academy.checklist.progress', { defaultValue: 'Your progress' })}
          value={checked.size}
          max={items.length}
          valueText={
            allChecked
              ? t('academy.checklist.allDone', { defaultValue: 'All {{total}} done', total: items.length })
              : t('academy.checklist.done', { defaultValue: '{{n}} of {{total}} done', n: checked.size, total: items.length })
          }
          tone={allChecked ? 'positive' : 'primary'}
        />
      )}

      {content.keyTakeaways.length > 0 && (
        <Box component="aside" sx={calloutSx}>
          <Box component="h2" sx={{ m: 0, mb: 1, fontSize: 16, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
            {t('academy.lessonPage.takeaways', { defaultValue: 'Key takeaways' })}
          </Box>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {content.keyTakeaways.map((takeaway, i) => (
              <Box component="li" key={i} sx={{ fontSize: 16, lineHeight: 1.6, color: hubColors.textPrimary, mb: 0.75, '&:last-child': { mb: 0 } }}>
                {takeaway}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {content.quiz.length > 0 && <QuizSection questions={content.quiz} lessonId={content.id} onQuizPassed={onQuizPassed} />}

      <LegalDisclaimer sources={content.sources} lastVerified={content.lastVerified} />
    </Box>
  );
}
